import { useState, useEffect, useRef, useCallback } from "react";
import { mediaUrl } from "lib/media";

/**
 * How to pay the fees, as an animated walkthrough rather than a PDF a parent
 * has to download and pinch-zoom.
 *
 * Everything shown comes from the `admission-resources` content blob, so the
 * office can re-shoot the screenshots or change the biller ID themselves when
 * the bank's app changes. The page picks which guide to pass in from the
 * institution tags; this component just renders the one it is given.
 *
 * It is open by default — paying the fees is the main thing the admissions page
 * is for while the online portal stays closed — and can be collapsed away.
 */

const HOLD_MS = 4200;

/** ASCII digits to Bangla, for the step counter in Bangla copy. */
const toBn = (n) => String(n).replace(/\d/g, (d) => "০১২৩৪৫৬৭৮৯"[d]);

/**
 * Uploaded screenshots live at the API origin; anything shipped in public/
 * is served by the site itself. In development those are different ports, so
 * sending a static path through mediaUrl() would 404.
 */
const imgSrc = (p) => (p?.startsWith("/uploads/") ? mediaUrl(p) : p);

const prefersReducedMotion = () =>
  !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export default function PaymentWalkthrough({ guide }) {
  const [open, setOpen] = useState(true);
  const [i, setI] = useState(0);
  // Decided on mount, so a visitor who asked for reduced motion gets a
  // walkthrough that waits for them rather than one already running.
  const [playing, setPlaying] = useState(() => !prefersReducedMotion());
  const panelRef = useRef(null);

  const steps = guide?.steps?.filter((s) => s?.image) || [];
  const count = steps.length;

  const go = useCallback((n) => setI((n + count) % count), [count]);

  useEffect(() => {
    if (!open || !playing || count < 2) return;
    const id = setTimeout(() => go(i + 1), HOLD_MS);
    return () => clearTimeout(id);
  }, [open, playing, i, go, count]);

  // A guide can lose steps in the editor while someone is looking at it.
  useEffect(() => {
    if (i >= count) setI(0);
  }, [i, count]);

  if (!count) return null;

  const show = () => {
    setOpen(true);
    setI(0);
    setPlaying(!prefersReducedMotion());
    // Let the panel render before moving focus into it.
    requestAnimationFrame(() => panelRef.current?.focus());
  };

  const title = guide.title || "ফি পরিশোধের নিয়ম";
  const titleEn = guide.titleEn || "How to pay the fees";

  if (!open) {
    return (
      <div>
        <button
          type="button"
          onClick={show}
          className="inline-flex items-center gap-2 min-h-[48px] rounded-lg bg-green px-5 py-3 font-semibold text-white hover:bg-green-dark transition-colors"
        >
          <span className="font-bn">পেমেন্ট পদ্ধতি দেখুন</span>
          <span aria-hidden="true">/</span>
          <span>Show payment procedure</span>
        </button>
      </div>
    );
  }

  const step = steps[Math.min(i, count - 1)];

  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      className="rounded-2xl border border-line bg-cream p-5 sm:p-6 focus-visible:outline-none"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h3 className="font-bn text-lg font-bold text-green-dark">{title}</h3>
          <p className="text-muted">{titleEn}</p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="min-h-[44px] px-3 underline text-green bg-transparent border-none cursor-pointer"
        >
          <span className="font-bn">বন্ধ করুন</span> / Close
        </button>
      </div>

      {/* The two things a parent must have in hand before they start. */}
      <div className="flex flex-wrap gap-3 mb-5">
        {guide.billerId && (
          <span className="rounded-lg bg-white border border-line px-3 py-2">
            <span className="text-muted">Biller ID </span>
            <strong className="text-green-dark text-lg">{guide.billerId}</strong>
          </span>
        )}
        {/* What the bank's app asks for. The school calls it the SSC roll; the
            label is editable because that is exactly the sort of thing that
            changes without warning. */}
        <span className="rounded-lg bg-white border border-line px-3 py-2">
          <span className="font-bn text-muted">{guide.identifierBn || "এসএসসি রোল"}</span>
          <span className="text-muted"> / {guide.identifierEn || "SSC Roll"}</span>
        </span>
      </div>

      <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start">
        {/* The phone. Every frame is stacked and cross-faded, so stepping back
            and forth never re-downloads or flashes. */}
        <div className="w-[240px] shrink-0">
          <div className="relative w-full aspect-[285/600] rounded-[22px] border-[6px] border-ink/80 bg-white overflow-hidden shadow-lg">
            {steps.map((s, n) => (
              <img
                key={`${s.image}-${n}`}
                src={imgSrc(s.image)}
                alt={n === i ? `${s.bn || ""} ${s.en || ""}`.trim() : ""}
                aria-hidden={n === i ? undefined : "true"}
                loading={n === 0 ? "eager" : "lazy"}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                  n === i ? "opacity-100" : "opacity-0"
                }`}
              />
            ))}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-green">
            <span className="font-bn">ধাপ {toBn(i + 1)}</span> / Step {i + 1} of {count}
          </p>

          {/* Announced to screen readers as the step changes. */}
          <div aria-live="polite" className="mt-2 mb-5">
            {step.bn && <p className="font-bn text-lg text-ink leading-relaxed">{step.bn}</p>}
            {step.en && <p className="text-muted mt-1 leading-relaxed">{step.en}</p>}
          </div>

          {count > 1 && (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setPlaying(false);
                    go(i - 1);
                  }}
                  className="min-h-[44px] rounded-lg border border-green px-4 font-semibold text-green bg-white"
                >
                  ← <span className="font-bn">পূর্ববর্তী</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPlaying(false);
                    go(i + 1);
                  }}
                  className="min-h-[44px] rounded-lg bg-green px-4 font-semibold text-white"
                >
                  <span className="font-bn">পরবর্তী</span> →
                </button>
                <button
                  type="button"
                  onClick={() => setPlaying((p) => !p)}
                  aria-pressed={playing}
                  className="min-h-[44px] px-3 underline text-green bg-transparent border-none cursor-pointer"
                >
                  {playing ? (
                    <>
                      <span className="font-bn">থামান</span> / Pause
                    </>
                  ) : (
                    <>
                      <span className="font-bn">চালান</span> / Play
                    </>
                  )}
                </button>
              </div>

              <div className="flex gap-2 mt-5 flex-wrap">
                {steps.map((s, n) => (
                  <button
                    key={`dot-${n}`}
                    type="button"
                    onClick={() => {
                      setPlaying(false);
                      go(n);
                    }}
                    aria-label={`Step ${n + 1}`}
                    aria-current={n === i ? "step" : undefined}
                    className={`h-2.5 rounded-full transition-all ${
                      n === i ? "w-7 bg-green" : "w-2.5 bg-line hover:bg-muted"
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {(guide.noteBn || guide.noteEn) && (
        <p className="text-muted text-sm mt-6 leading-relaxed">
          {guide.noteBn && <span className="font-bn">{guide.noteBn}</span>}{" "}
          {guide.noteEn}
        </p>
      )}
    </div>
  );
}
